import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { FRAME_SLACK_MS, ROW_EXIT_SLIDE_MS } from "./motion";
import { Notification } from "./types";

// Keeps dismissed rows on screen just long enough to play their exit animation.
//
// The context mutation is never delayed: callers dismiss optimistically first
// (badge and `items` update immediately), and this hook only holds a read-only
// "ghost" of each dismissed row in the *rendered* list until its exit ends. The
// ghost sits in the slot it occupied, so nothing reflows mid-animation. Once a
// batch finishes, its ghosts are dropped in one commit, preceded by
// `beforeRemove` (the FLIP capture) so the siblings can glide into the gap.
//
// Removal is timer-driven rather than animationend-driven so a batch always
// resolves: a staggered Clear all must drop every ghost together (dropping them
// one by one would shift the still-animating rows), and reduced-motion CSS or a
// backgrounded tab can swallow animation events.

// Re-insert ghosts at their previous positions. Walk the last committed list and
// place each ghost directly after the nearest live row that preceded it, so the
// order survives several overlapping exits and rows prepended mid-exit.
const withGhosts = (
  previous: Notification[],
  items: Notification[],
  exiting: ReadonlyMap<string, number>
): Notification[] => {
  if (exiting.size === 0) {
    return items;
  }
  const live = new Set(items.map((n) => n.id));
  const result = items.slice();
  let cursor = -1;
  previous.forEach((n) => {
    if (live.has(n.id)) {
      cursor = result.findIndex((r) => r.id === n.id);
    } else if (exiting.has(n.id)) {
      cursor += 1;
      result.splice(cursor, 0, n);
    }
  });
  return result;
};

export const useExitingRows = (items: Notification[], beforeRemove?: () => void) => {
  // id → stagger delay (ms) of each row that is animating out.
  const [exiting, setExiting] = useState<ReadonlyMap<string, number>>(() => new Map());

  // What the list looked like at the last commit, ghosts included.
  const committed = useRef<Notification[]>(items);
  const displayItems = useMemo(() => withGhosts(committed.current, items, exiting), [items, exiting]);
  useLayoutEffect(() => {
    committed.current = displayItems;
  }, [displayItems]);

  const beforeRemoveRef = useRef(beforeRemove);
  beforeRemoveRef.current = beforeRemove;

  const timers = useRef(new Set<number>());
  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach((t) => window.clearTimeout(t));
  }, []);

  /** Start exiting a batch of rows (id → stagger delay in ms). */
  const exitRows = useCallback((delays: ReadonlyMap<string, number>) => {
    if (delays.size === 0) {
      return;
    }
    setExiting((prev) => {
      const next = new Map(prev);
      delays.forEach((delay, id) => next.set(id, delay));
      return next;
    });

    let longestDelay = 0;
    delays.forEach((delay) => {
      longestDelay = Math.max(longestDelay, delay);
    });
    // The fade (200ms) finishes before the slide (220ms), so the ghost is already
    // invisible when it is removed even if the timer beats the last frame.
    const timer = window.setTimeout(() => {
      timers.current.delete(timer);
      beforeRemoveRef.current?.();
      setExiting((prev) => {
        const next = new Map(prev);
        delays.forEach((_, id) => next.delete(id));
        return next;
      });
    }, longestDelay + ROW_EXIT_SLIDE_MS + FRAME_SLACK_MS);
    timers.current.add(timer);
  }, []);

  // A ghost whose dismiss failed is back in `items` (the context re-inserts it), so
  // it renders as a live row again and its exit class drops off.
  const liveIds = useMemo(() => new Set(items.map((n) => n.id)), [items]);
  const exitDelayOf = useCallback((id: string) => (liveIds.has(id) ? undefined : exiting.get(id)), [exiting, liveIds]);

  return { displayItems, exitRows, exitDelayOf };
};

export default useExitingRows;
